import { api } from "../api";
import { callIdForOffice, markSeen } from "../dedup/callDedup";
import { callIdForPassword } from "../dedup/callDedup";
import { telemetry } from "../telemetry/telemetry";

type Listener<T> = (items: T[]) => void;

const passwordListeners = new Set<Listener<any>>();
const officeListeners = new Set<Listener<any>>();

export function onPasswordsRehydrate(fn: Listener<any>): () => void {
  passwordListeners.add(fn);
  return () => passwordListeners.delete(fn);
}

export function onOfficesRehydrate(fn: Listener<any>): () => void {
  officeListeners.add(fn);
  return () => officeListeners.delete(fn);
}

function getPanelId(): string | null {
  try {
    const raw = localStorage.getItem("panel@drclick");
    if (!raw) return null;
    return JSON.parse(raw)?.idpainelchamado ?? null;
  } catch {
    return null;
  }
}

export async function rehydrateAll(): Promise<void> {
  const panelId = getPanelId();
  if (!panelId) return;

  const results = await Promise.allSettled([
    api.get(`/users-patients/passwordsbygroups?idpainelchamado=${panelId}`),
    api.get(`/users-patients/patient-office-bypanelid?idpainelchamado=${panelId}`),
  ]);

  const [pwds, offs] = results;

  if (pwds.status === "fulfilled") {
    const items = pwds.value?.data?.data ?? [];
    if (Array.isArray(items)) {
      for (const item of items) {
        // Marca como visto sem disparar audio (rehydrate, nao socket).
        markSeen(callIdForPassword(item));
      }
      telemetry("rehydrate_used", { kind: "passwords", count: items.length });
      for (const fn of passwordListeners) {
        try {
          fn(items);
        } catch (err) {
          console.error("[panel:rehydrate_listener_error] passwords", err);
        }
      }
    }
  } else {
    telemetry("rehydrate_failed", {
      kind: "passwords",
      reason: pwds.reason?.message,
    });
  }

  if (offs.status === "fulfilled") {
    const items = offs.value?.data?.data ?? [];
    if (Array.isArray(items)) {
      for (const item of items) {
        const consultorio = item?.consultorio?.idconsultorio;
        const paciente = item?.paciente?.nomesocial?.length
          ? item.paciente.nomesocial
          : item?.paciente?.nomepaciente;
        if (consultorio && paciente) {
          markSeen(
            callIdForOffice({
              panels: [{ idconsultorio: consultorio }],
              nomepaciente: paciente,
            })
          );
        }
      }
      telemetry("rehydrate_used", { kind: "offices", count: items.length });
      for (const fn of officeListeners) {
        try {
          fn(items);
        } catch (err) {
          console.error("[panel:rehydrate_listener_error] offices", err);
        }
      }
    }
  } else {
    telemetry("rehydrate_failed", {
      kind: "offices",
      reason: offs.reason?.message,
    });
  }
}
